/**
 * Closed-enum registry of canonical marketing-website conversion + telemetry
 * events. Per the MAS-001 plan, every analytics event a marketing-website
 * emits MUST be one of these keys. New events go through a registry
 * update + cross-app contract test bump, never an inline string.
 *
 * Namespacing: events fire under `marketing.<appKey>.<eventName>` (the
 * factory wraps the bare name; consumers pass only the bare name).
 *
 * @module @puffin-core/marketing-shell/analytics/conversionEvents
 */

export const CONVERSION_EVENTS = Object.freeze([
  // Engagement
  'page_view',
  'hero_cta_clicked',
  'feature_cta_clicked',
  'pricing_tier_viewed',
  'pricing_cta_clicked',
  'faq_expanded',
  'demo_modal_opened',
  // Contact form lifecycle
  'contact_form_started',
  'contact_form_submitted',
  'contact_form_failed',
  // Chrome
  'locale_changed',
  'ecosystem_switched',
  'outbound_clicked',
  // Blog
  'blog_post_viewed',
  'blog_category_browsed',
  // Consent
  'cookie_consent_set',
  // A/B harness
  'variant_assigned',
  // Web Vitals (per webVitalsAdapter)
  'web_vitals',
  // Phase 28 H2 (2026-05-19) — scroll-depth milestone (25/50/75/100).
  // Surfaced by AnalyticsProvider.trackScrollDepth(); the analyticsProvider
  // emits this as a canonical conversion event so the closed-cardinality
  // namespace registry stays the SSOT.
  'scroll_depth',
]);

/**
 * Named constants for typo-safe call sites — `CONVERSION_EVENT.OUTBOUND_CLICKED` — the twin of
 * storefront-cms's `COMMERCE_EVENT`. DERIVED from `CONVERSION_EVENTS`, so the list stays the one
 * source (LIFE-SHORTLISTED-001 P9.1).
 * @type {Readonly<Record<string, string>>}
 */
export const CONVERSION_EVENT = Object.freeze(
  Object.fromEntries(CONVERSION_EVENTS.map((name) => [name.toUpperCase(), name])),
);

/**
 * @param {string} eventName
 * @returns {boolean}
 */
export function isConversionEvent(eventName) {
  return typeof eventName === 'string' && CONVERSION_EVENTS.includes(eventName);
}

/**
 * Build the namespaced event key for a given app + event.
 *
 * @param {string} appKey
 * @param {string} eventName
 * @returns {string}
 */
export function buildEventKey(appKey, eventName) {
  if (typeof appKey !== 'string' || !appKey) {
    throw new TypeError('buildEventKey: appKey (string) is required');
  }
  if (!isConversionEvent(eventName)) {
    throw new TypeError(`buildEventKey: "${eventName}" is not a registered conversion event`);
  }
  return `marketing.${appKey}.${eventName}`;
}
